<script setup lang="ts">
    import formKitConfig from '@/formkit';
    import { type GuestTypeTyping, type ProgrameeTyping } from '@/typings/widget';
    import { FormKitProvider, FormKitRoot, defaultConfig } from '@formkit/vue';
    import NumberInput from '@shared/components/form/number-input.vue';
    import { DateTime } from '@shared/helpers';
    import { type Ref } from 'vue';

    const props = defineProps<{
        modelValue: ProgrameeTyping[]
        guestTypes?: GuestTypeTyping[]
        guestAmount: number
        guestChanged?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: ProgrameeTyping[]): void
        (event: 'next'): void
    }>();

    const { isMobile } = useAppBreakpoints();
    const { t } = useI18n();

    // Refs
    const programModel: Ref<ProgrameeTyping[]> = ref(props.modelValue ?? []);

    // Computed
    const guestTypeLabels: ComputedRef<string[]> = computed(() => {
        const allLabels: string[] = [];
        props.guestTypes?.map(guestType => allLabels.push(guestType.name));

        return allLabels;
    });

    // Methods
    const buildGuestTypeAmounts = ():void => {
        if (programModel.value) {
            programModel.value = programModel.value.map(day => {
                if (day.timeslots.length) {
                    day.timeslots.map(timeslot => {
                        // If don't have guest type amounts or if guest amount changed in previous step, prefill it,
                        if (!timeslot.guestTypeAmounts?.length
                            || (timeslot.guestTypeAmounts?.length && timeslot.guestTypeAmounts?.length !== guestTypeLabels.value.length)
                            || props.guestChanged) {
                            timeslot.guestTypeAmounts = props.guestTypes?.map(guestType => ({
                                amount: props.guestAmount,
                                guestType,
                            }));
                        }
                        return timeslot;
                    });
                }
                return day;
            });
        }
    };

    // Hooks
    onMounted(() => {
        buildGuestTypeAmounts();
    });

    // Watchers
    watch(() => programModel.value, nVal => {
        emit('update:modelValue', nVal);
    });
</script>

<template>
    <div class="program-selection">
        <div class="program-selection__grid">
            <div
                v-for="(day, dayIndex) in programModel"
                :key="`${dayIndex}-day`"
                class="program-selection__day"
            >
                <div class="program-selection__day__header">
                    <div class="program-selection__day__header-content">
                        <label
                            v-for="label in guestTypeLabels"
                            :key="label"
                        >
                            {{ label }}.
                        </label>
                    </div>
                </div>
                <div class="program-selection__day__content">
                    <div
                        v-for="timeslot in day.timeslots"
                        :key="timeslot.id"
                        class="program-selection__timeslot"
                    >
                        <div class="program-selection__timeslot__info">
                            <div class="program-selection__timeslot__content">
                                <div class="program-selection__timeslot__time">
                                    {{ DateTime.dayjsObject(timeslot.startDateTime).format('HH:mm') }}
                                    <template v-if="timeslot?.endDateTime">
                                        -
                                        {{ DateTime.dayjsObject(timeslot.endDateTime).format('HH:mm') }}
                                    </template>
                                </div>
                                <span class="program-selection__timeslot__name">
                                    {{ timeslot.name }}
                                </span>
                            </div>
                            <div class="program-selection__timeslot__guest-types-mobile">
                                <FormKitProvider :config="defaultConfig(formKitConfig)">
                                    <FormKitRoot>
                                        <div
                                            v-for="(guestTypeAmount, guestTypeAmountIndex) in timeslot.guestTypeAmounts"
                                            :key="`${guestTypeAmountIndex}-guest-type-amount`"
                                            class="program-selection__timeslot__guest-type"
                                        >
                                            <NumberInput
                                                v-model="guestTypeAmount.amount"
                                                :label="guestTypeAmount.guestType?.name"
                                                name="amount"
                                                class="guest-input"
                                                secondary
                                            />
                                        </div>
                                    </FormKitRoot>
                                </FormKitProvider>
                            </div>
                        </div>
                        <div class="program-selection__timeslot__guest-types">
                            <FormKitProvider :config="defaultConfig(formKitConfig)">
                                <FormKitRoot>
                                    <div
                                        v-for="(guestTypeAmount, guestTypeAmountIndex) in timeslot.guestTypeAmounts"
                                        :key="`${guestTypeAmountIndex}-guest-type-amount`"
                                        class="program-selection__timeslot__guest-type"
                                    >
                                        <NumberInput
                                            v-model="guestTypeAmount.amount"
                                            :label="isMobile ? guestTypeAmount.guestType?.name : undefined"
                                            name="amount"
                                            class="guest-input"
                                            secondary
                                        />
                                    </div>
                                </FormKitRoot>
                            </FormKitProvider>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <button
            type="button"
            class="btn program-selection__next btn--primary"
            @click="emit('next')"
        >
            <span>
                {{ t('widget.buttons.next') }}
            </span>
        </button>
    </div>
</template>

<style lang="scss" scoped>
.program-selection {
    block-size: 100%;
    inline-size: 100%;
    padding-inline: var(--spacing-03);

    @include min-width(lg) {
        padding-inline: var(--spacing-07);
    }

    &__grid,
    &__day__content {
        display: flex;
        flex-direction: column;
    }

    &__grid {
        gap: var(--spacing-04);
        padding-block: var(--spacing-04) var(--spacing-16);

        @include min-width(md) {
            gap: var(--spacing-05);
            padding-block-start: var(--spacing-07);
        }

        @include min-width(lg) {
            gap: var(--spacing-07);
            padding-block-start: var(--spacing-01);
        }
    }

    &__day {
        &__header {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-02);
            padding-inline: var(--spacing-03);

            @include max-width(md) {
                display: none;
            }

            &::before {
                content: '';
            }
        }

        &__header-content {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-02);

            label {
                flex: 1;
                color: var(--text-tertiary);
                font-size: var(--font-regular);

                @include min-width(md) {
                    inline-size: 100px;
                }
            }

            @include min-width(md) {
                justify-content: flex-start;
            }
        }

        &__content {
            padding: var(--spacing-03);
            border: 1px solid var(--border-quaternary);
            border-radius: var(--border-radius-md);
            gap: var(--spacing-02);
        }
    }

    &__timeslot__info,
    &__day__header::before {
        display: flex;
        flex: 2;
        gap: var(--spacing-03);
        min-inline-size: 0;
    }

    &__timeslot {
        display: flex;
        justify-content: space-between;

        @include min-width(md) {
            gap: var(--spacing-02);
        }

        :deep(.formkit-label) {
            color: var(--text-tertiary);
            margin-block-end: var(--spacing-01);
        }

        &__content,
        &__guest-types-mobile,
        &__guest-types {
            display: flex;
            justify-content: space-between;
            gap: var(--spacing-02);
            inline-size: 100%;
        }

        &__info {
            flex-direction: column;
            padding: var(--spacing-03);
            border-radius: var(--border-radius-xs);
            background: var(--background-secondary);
            inline-size: 100%;

            @include min-width(md) {
                flex-direction: row;
                align-items: center;
                block-size: var(--size-12);
            }

            @include min-width(lg) {
                flex: 2;
                block-size: var(--size-13);
            }

            .guest-input {
                flex: 1;
            }
        }

        &__guest-types {
            @include max-width(md) {
                display: none;
            }

            @include min-width(md) {
                inline-size: auto;
            }
        }

        &__guest-type {
            @include max-width(md) {
                flex: 1;
            }

            @include min-width(md) {
                inline-size: 100px;
            }
        }

        &__guest-types-mobile {
            @include min-width(md) {
                display: none;
            }
        }

        &__time {
            font-size: var(--font-regular);
            inline-size: 80px;
            white-space: nowrap;

            @include min-width(md) {
                inline-size: 102px;
            }
        }

        &__name {
            overflow: hidden;
            flex: 2;
            font-size: var(--font-regular);
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__next {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        align-self: center;
        inset-block-end: var(--spacing-05);
        inset-inline-start: 50%;
        min-inline-size: 174px;
        transform: translateX(-50%);

        @include min-width(lg) {
            inset-block-end: var(--spacing-04);
            inset-inline: auto var(--spacing-04);
            transform: none;
        }
    }
}
</style>
